import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Play, 
  Calendar, 
  Clock, 
  Eye, 
  Trash2, 
  Video as VideoIcon,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import { api, useAuth } from '../hooks/useAuth';
import { formatSessionTicketContext, type SessionLinkedTicket } from '../utils/ticketDisplay';
import {
  buildTempStreamPath,
  getSourceClips,
  normalizeSessionVideo,
  resolveFinalVideo,
} from '../utils/sessionVideos';
import { useTickets } from '../hooks/useTickets';
import LoadingSpinner from '../components/LoadingSpinner';
import VideoPlayer from '../components/VideoPlayer';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Button from '../components/Button';
import toast from 'react-hot-toast';

interface Video {
  id: string;
  filename: string;
  originalFilename: string;
  filePath: string;
  fileSize: number;
  durationSeconds: number;
  resolution: string;
  format: string;
  createdAt: string;
}

interface Session {
  id: string;
  phobiaTypeId?: string | null;
  phobiaTypeName?: string | null;
  phobiaTypeDescription?: string | null;
  ticket?: SessionLinkedTicket | null;
  title: string;
  description?: string;
  sessionDate: string;
  durationMinutes?: number;
  anxietyLevelStart?: number;
  anxietyLevelEnd?: number;
  notes?: string;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  videos: Video[];
  videoCount: number;
  totalDuration: number;
}

const Sessions: React.FC = () => {
  const { user } = useAuth();
  const isCustomerSupport = user && (
    user.role?.toLowerCase() === 'support' ||
    user.role?.toLowerCase() === 'customer support'
  );
  
  const isVideoRecorderRole = user && (
    user.role?.toLowerCase() === 'video_recorder' ||
    user.role?.toLowerCase() === 'video recorder' ||
    user.role?.toLowerCase() === 'game_master'
  );
  
  // For video recorder, check if they have open tickets in the queue
  const { data: ticketsData } = useTickets();
  const tickets = ticketsData?.tickets || [];
  const myPendingTickets = isVideoRecorderRole
    ? tickets.filter((ticket) => {
        const s = (ticket.status ?? '').toLowerCase();
        const isOpen = s === 'open' || s === 'pending';
        return isOpen && (!ticket.assignedTo || ticket.assignedTo.id === user?.id);
      })
    : [];
  const hasPendingTickets = myPendingTickets.length > 0;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [selectedStreamPath, setSelectedStreamPath] = useState<string | undefined>();
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    sessionId: string | null;
    sessionTitle: string;
  }>({
    isOpen: false,
    sessionId: null,
    sessionTitle: ''
  });
  const [deleteVideoDialog, setDeleteVideoDialog] = useState<{
    isOpen: boolean;
    videoId: string | null;
    videoTitle: string;
  }>({
    isOpen: false,
    videoId: null,
    videoTitle: ''
  });
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const queryClient = useQueryClient();
  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
  };

  // Fetch videos
  const { data: sessions, isLoading, error } = useQuery<Session[]>(
    'sessions',
    async () => {
      try {
        const response = await api.get('/sessions');
        const data = response.data;

        if (Array.isArray(data)) {
          return data;
        } else if (data && Array.isArray(data.sessions)) {
          return data.sessions;
        } else if (data && Array.isArray(data.data)) {
          return data.data;
        } else {
          console.warn('Unexpected API response format:', data);
          return [];
        }
      } catch (err: any) {
        console.error('Error fetching videos:', err);
        console.error('Error response:', err.response);
        // If it's an authentication error, return empty array
        if (err.response?.status === 401) {
          console.warn('User not authenticated, returning empty videos');
          return [];
        }
        // For other errors, throw to trigger error state
        throw err;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  // Delete session mutation
  const deleteSessionMutation = useMutation(
    async (sessionId: string) => {
      const response = await api.delete(`/sessions/${sessionId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('in-progress-videos');
        queryClient.invalidateQueries('user-stats');
        toast.success('Session deleted successfully');
      },
      onError: (error: any) => {
        console.error('Failed to delete session:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        let errorMessage = 'Failed to delete session';
        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      },
    }
  );

  // Delete individual video mutation
  const deleteVideoMutation = useMutation(
    async (videoId: string) => {
      const response = await api.delete(`/videos/${videoId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('in-progress-videos');
        queryClient.invalidateQueries('user-stats');
        toast.success('Video deleted successfully');
      },
      onError: (error: any) => {
        console.error('Failed to delete video:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        let errorMessage = 'Failed to delete video';
        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      },
    }
  );

  // Cancel/Update session status mutation
  const updateSessionStatusMutation = useMutation(
    async ({ sessionId, status }: { sessionId: string; status: string }) => {
      const response = await api.put(`/sessions/${sessionId}`, { status });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('in-progress-videos');
        queryClient.invalidateQueries('user-stats');
        toast.success('Session cancelled successfully');
      },
      onError: (error: any) => {
        console.error('Failed to update session status:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        let errorMessage = 'Failed to update session status';
        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      },
    }
  );

  const handleCancelSession = (sessionId: string, sessionTitle: string) => {
    console.log('Cancelling session:', { sessionId, sessionTitle });
    updateSessionStatusMutation.mutate({ sessionId, status: 'cancelled' });
  };

  // Filter videos - ensure sessions is always an array
  const sessionsArray = Array.isArray(sessions) ? sessions : [];
  const filteredSessions = sessionsArray.filter(session => {
    const ticketContext = formatSessionTicketContext(session.ticket)?.toLowerCase() || '';
    const matchesSearch = session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         session.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (session.phobiaTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
                         ticketContext.includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    const isRoomTicket = !!session.ticket;
    const matchesSource =
      sourceFilter === 'all' ||
      (sourceFilter === 'room' && isRoomTicket) ||
      (sourceFilter === 'legacy' && !isRoomTicket && !!session.phobiaTypeName) ||
      (sourceFilter === 'other' && !isRoomTicket && !session.phobiaTypeName);
    
    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleDeleteSession = (sessionId: string, sessionTitle: string) => {
    setDeleteDialog({
      isOpen: true,
      sessionId,
      sessionTitle
    });
  };

  const handleDeleteVideo = (videoId: string, videoTitle: string) => {
    setDeleteVideoDialog({
      isOpen: true,
      videoId,
      videoTitle
    });
  };

  const confirmDeleteVideo = () => {
    if (deleteVideoDialog.videoId) {
      deleteVideoMutation.mutate(deleteVideoDialog.videoId);
      setDeleteVideoDialog({
        isOpen: false,
        videoId: null,
        videoTitle: ''
      });
    }
  };

  const cancelDeleteVideo = () => {
    setDeleteVideoDialog({
      isOpen: false,
      videoId: null,
      videoTitle: ''
    });
  };

  const confirmDeleteSession = () => {
    if (deleteDialog.sessionId) {
      deleteSessionMutation.mutate(deleteDialog.sessionId);
      setDeleteDialog({
        isOpen: false,
        sessionId: null,
        sessionTitle: ''
      });
    }
  };

  const cancelDeleteSession = () => {
    setDeleteDialog({
      isOpen: false,
      sessionId: null,
      sessionTitle: ''
    });
  };

  // Delete all videos mutation
  const deleteAllSessionsMutation = useMutation(
    async () => {
      await api.delete('/sessions/delete-all');
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sessions');
        toast.success('All videos deleted successfully');
      },
      onError: () => {
        toast.error('Failed to delete all videos');
      },
    }
  );

  const handleDeleteAllSessions = () => {
    setDeleteAllDialog(true);
  };

  const confirmDeleteAllSessions = () => {
    deleteAllSessionsMutation.mutate();
    setDeleteAllDialog(false);
  };

  const cancelDeleteAllSessions = () => {
    setDeleteAllDialog(false);
  };

  const handlePlayVideo = (video: Video, streamPath?: string) => {
    setSelectedStreamPath(streamPath);
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
    setSelectedStreamPath(undefined);
  };

  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'planned':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDescription = (status: string, session: Session) => {
    switch (status) {
      case 'planned':
        const sessionDate = new Date(session.sessionDate);
        const now = new Date();
        const daysUntil = Math.ceil((sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntil < 0) {
          return `Overdue by ${Math.abs(daysUntil)} day(s)`;
        } else if (daysUntil === 0) {
          return 'Scheduled for today';
        } else if (daysUntil === 1) {
          return 'Scheduled for tomorrow';
        } else {
          return `Scheduled in ${daysUntil} days`;
        }
      case 'in_progress':
        const startTime = session.updatedAt || session.createdAt;
        const startDate = new Date(startTime);
        const currentTime = new Date();
        const minutesSinceStart = Math.floor((currentTime.getTime() - startDate.getTime()) / (1000 * 60));
        
        if (minutesSinceStart < 60) {
          return `Active for ${minutesSinceStart} minute(s)`;
        } else {
          const hours = Math.floor(minutesSinceStart / 60);
          const minutes = minutesSinceStart % 60;
          return `Active for ${hours}h ${minutes}m`;
        }
      case 'completed':
        return session.videoCount > 0 ? `${session.videoCount} video(s) recorded` : 'No videos recorded';
      case 'cancelled':
        return 'This session was cancelled';
      default:
        return '';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planned':
        return '📅';
      case 'in_progress':
        return '⏳';
      case 'completed':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '●';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds?: number | null) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Show authentication required message if user is not logged in
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-content text-center py-12">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-yellow-100 mb-4">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-medium text-yellow-900 mb-2">Authentication Required</h3>
            <p className="text-yellow-700 mb-6">
              You need to be logged in to view your ETR videos.
            </p>
            <Link to="/login">
              <Button variant="primary" size="md" leftIcon={<AlertCircle className="h-4 w-4" />}>
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-content text-center py-12">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-yellow-100 mb-4">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-medium text-yellow-900 mb-2">Authentication Required</h3>
            <p className="text-yellow-700 mb-6">
              Please log in to view your ETR exposure therapy videos.
            </p>
            <Link to="/login">
              <Button variant="primary" size="md" leftIcon={<AlertCircle className="h-4 w-4" />}>
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border-red-200">
        <div className="card-content">
          <div className="flex items-center space-x-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error Loading Videos</span>
          </div>
          <p className="mt-2 text-sm text-red-700">
            Failed to load videos. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="etr-page overflow-visible">
      {/* Header */}
      <div className="etr-page-header mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="etr-page-title mb-2">ETR Videos</h1>
            <p className="etr-page-subtitle">Manage your ETR exposure therapy videos.</p>
          </div>
          <div className="flex items-center space-x-4">
            {user && (user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'administrator') && (
              <Button
                variant="outline"
                size="md"
                onClick={handleDeleteAllSessions}
                disabled={deleteAllSessionsMutation.isLoading || sessionsArray.length === 0}
                leftIcon={<Trash2 className="h-4 w-4" />}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 w-full sm:w-auto"
              >
                <span className="hidden sm:inline">Delete All Videos</span>
                <span className="sm:hidden">Delete All</span>
              </Button>
            )}
            {!isCustomerSupport && !isVideoRecorderRole && (
              <Link to="/sessions/new" className="w-full sm:w-auto">
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="h-4 w-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">New ETR Video</span>
                  <span className="sm:hidden">New Video</span>
                </Button>
              </Link>
            )}
            {isVideoRecorderRole && hasPendingTickets && (
              <Link 
                to={`/sessions/new?ticketId=${myPendingTickets[0].id}`} 
                className="w-full sm:w-auto"
              >
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="h-4 w-4" />}
                  className="w-full sm:w-auto"
                >
                  <span className="hidden sm:inline">Create New Video from Ticket</span>
                  <span className="sm:hidden">New Video</span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="etr-surface p-4 sm:p-6 mb-6 overflow-visible">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-medium text-gray-700">Filter Videos</h3>
            {(searchTerm || statusFilter !== 'all' || sourceFilter !== 'all') && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {[searchTerm, statusFilter !== 'all' ? statusFilter : null, sourceFilter !== 'all' ? sourceFilter : null].filter(Boolean).length} active
              </span>
            )}
          </div>
          {(searchTerm || statusFilter !== 'all' || sourceFilter !== 'all') && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center space-x-1 self-start sm:self-auto"
            >
              <XCircle className="h-4 w-4" />
              <span>Clear Filters</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 ${
                searchTerm ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative overflow-visible z-10">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 appearance-none cursor-pointer ${
                statusFilter !== 'all' ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
              }`}
              style={{ zIndex: 10 }}
            >
              <option value="all">All Status</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Source filter */}
          <div className="relative overflow-visible z-10">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 appearance-none cursor-pointer ${
                sourceFilter !== 'all' ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
              }`}
              style={{ zIndex: 10 }}
            >
              <option value="all">All sources</option>
              <option value="room">Room tickets</option>
              <option value="legacy">Legacy ETR type</option>
              <option value="other">Other</option>
            </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
        </div>
      </div>

      {/* Videos List */}
      {filteredSessions.length === 0 ? (
        <div className="card">
          <div className="card-content text-center py-12">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No videos found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all' || sourceFilter !== 'all'
                ? 'Try adjusting your filters to see more videos.'
                : isVideoRecorderRole
                ? 'You don\'t have any videos yet. Create videos from your assigned tickets.'
                : isCustomerSupport
                ? 'You don\'t have any videos yet.'
                : 'Get started by creating your first ETR exposure therapy video.'
              }
            </p>
            {!isVideoRecorderRole && !isCustomerSupport && (
              <Link to="/sessions/new">
                <Button
                  variant="primary"
                  size="lg"
                  leftIcon={<Plus className="h-5 w-5" />}
                  className="mx-auto"
                >
                  Create First Video
                </Button>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <div key={session.id} className="card hover:shadow-lg transition-shadow">
              <div className="card-header">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {session.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {session.description || 'No description provided'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)} flex items-center gap-1`}>
                      <span>{getStatusIcon(session.status)}</span>
                      <span>{session.status.replace('_', ' ')}</span>
                    </span>
                    {getStatusDescription(session.status, session) && (
                      <span className="text-xs text-gray-500 mt-1">
                        {getStatusDescription(session.status, session)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-content">
                <div className="space-y-3">
                  {/* ETR Type */}
                  {formatSessionTicketContext(session.ticket) ? (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{formatSessionTicketContext(session.ticket)}</span>
                    </div>
                  ) : session.phobiaTypeName ? (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{session.phobiaTypeName} (legacy)</span>
                    </div>
                  ) : null}

                  {session.anxietyLevelStart != null && session.anxietyLevelStart > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Exposure Level:</span>
                      <div className="flex space-x-1">
                        {[...Array(10)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-2 h-2 rounded-full ${
                              i < session.anxietyLevelStart! ? 'bg-primary-600' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {session.anxietyLevelStart}/10
                      </span>
                    </div>
                  </div>
                  )}

                  {/* Duration and Video Count */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatDuration(session.totalDuration)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <VideoIcon className="h-4 w-4" />
                      <span>{session.videoCount} video(s)</span>
                    </div>
                  </div>

                  {/* Videos Section */}
                  {session.videos && session.videos.length > 0 && (
                    <div className="border-t pt-3">
                      <button
                        onClick={() => toggleSessionExpansion(session.id)}
                        className="flex items-center justify-between w-full text-sm text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        <span className="flex items-center space-x-1">
                          <VideoIcon className="h-4 w-4" />
                          <span>View Videos ({session.videoCount})</span>
                        </span>
                        <span className={`transform transition-transform ${expandedSession === session.id ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      
                      {expandedSession === session.id && (() => {
                        const finalVideo = resolveFinalVideo(session.videos, session.ticket);
                        const sourceClips =
                          session.status === 'completed' && finalVideo
                            ? getSourceClips(session.videos)
                            : session.videos;
                        const renderRow = (video: Video, streamPath?: string, highlight?: boolean) => (
                          <div
                            key={video.id}
                            className={`flex items-center justify-between p-2 rounded-lg ${
                              highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {video.originalFilename}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDuration(video.durationSeconds)} • {video.resolution}
                              </p>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handlePlayVideo(video, streamPath)}
                                className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                title="Play video"
                              >
                                <Play className="h-4 w-4" />
                              </button>
                              {user &&
                                (user.role?.toLowerCase() === 'admin' ||
                                  user.role?.toLowerCase() === 'administrator') &&
                                video.id !== 'combined-final' && (
                                  <button
                                    onClick={() => handleDeleteVideo(video.id, video.originalFilename)}
                                    className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                    title="Delete video"
                                    disabled={deleteVideoMutation.isLoading}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                            </div>
                          </div>
                        );
                        return (
                          <div className="mt-3 space-y-2">
                            {finalVideo && session.status === 'completed' && (
                              <>
                                <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                                  Final video
                                </p>
                                {renderRow(
                                  normalizeSessionVideo(finalVideo),
                                  buildTempStreamPath(finalVideo.filename),
                                  true
                                )}
                                {sourceClips.length > 0 && (
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">
                                    Source clips ({sourceClips.length})
                                  </p>
                                )}
                              </>
                            )}
                            {sourceClips.map((video) => renderRow(normalizeSessionVideo(video)))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Created Date */}
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>Created {formatDate(session.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <div className="flex items-center justify-between">
                  <Link to={`/sessions/${session.id}`}>
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Eye className="h-3 w-3" />}
                    >
                      View
                    </Button>
                  </Link>
                  
                  <div className="flex items-center space-x-2">
                    {/* Cancel button for in_progress sessions */}
                    {session.status === 'in_progress' && (
                      <button
                        onClick={() => handleCancelSession(session.id, session.title)}
                        className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                        title="Cancel session"
                        disabled={updateSessionStatusMutation.isLoading}
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    )}
                    {user && (user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'administrator') && (
                      <button
                        onClick={() => handleDeleteSession(session.id, session.title)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {sessionsArray.length > 0 && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="card">
            <div className="card-content text-center">
              <div className="text-2xl font-bold text-gray-900">{sessionsArray.length}</div>
              <div className="text-sm text-gray-600">Total Videos</div>
            </div>
          </div>
          <div className="card">
            <div className="card-content text-center">
              <div className="text-2xl font-bold text-green-600">
                {sessionsArray.filter(s => s.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
          </div>
          <div className="card">
            <div className="card-content text-center">
              <div className="text-2xl font-bold text-blue-600">
                {sessionsArray.filter(s => s.status === 'in_progress').length}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
              <div className="text-xs text-gray-500 mt-1">
                {sessionsArray.filter(s => s.status === 'in_progress').length === 0 
                  ? 'No active sessions' 
                  : sessionsArray.filter(s => s.status === 'in_progress').length === 1
                  ? '1 active session'
                  : `${sessionsArray.filter(s => s.status === 'in_progress').length} active sessions`}
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-content text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {sessionsArray.filter(s => s.status === 'planned').length}
              </div>
              <div className="text-sm text-gray-600">Planned</div>
              <div className="text-xs text-gray-500 mt-1">
                {sessionsArray.filter(s => s.status === 'planned').length === 0 
                  ? 'No scheduled sessions' 
                  : sessionsArray.filter(s => s.status === 'planned').length === 1
                  ? '1 upcoming session'
                  : `${sessionsArray.filter(s => s.status === 'planned').length} upcoming sessions`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          video={selectedVideo}
          streamPath={selectedStreamPath}
          onClose={handleCloseVideo}
          isModal={true}
        />
      )}

      {/* Delete Video Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDeleteSession}
        onConfirm={confirmDeleteSession}
        title="Delete Video"
        message={`Are you sure you want to delete the video "${deleteDialog.sessionTitle}"? This action cannot be undone and will also delete all associated videos and data.`}
        confirmText="Delete Video"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteSessionMutation.isLoading}
      />

      {/* Delete Video Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteVideoDialog.isOpen}
        onClose={cancelDeleteVideo}
        onConfirm={confirmDeleteVideo}
        title="Delete Video"
        message={`Are you sure you want to delete the video "${deleteVideoDialog.videoTitle}"? This action cannot be undone and will permanently remove the video file.`}
        confirmText="Delete Video"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteVideoMutation.isLoading}
      />

      {/* Delete All Videos Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteAllDialog}
        onClose={cancelDeleteAllSessions}
        onConfirm={confirmDeleteAllSessions}
        title="Delete All Videos"
        message={`Are you sure you want to delete ALL ${sessionsArray.length} videos? This action cannot be undone and will permanently remove all videos and associated data.`}
        confirmText="Delete All Videos"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteAllSessionsMutation.isLoading}
      />

    </div>
  );
};

export default Sessions;

